// ============================================================================
// EMAIL TEMPLATES HOOK — useSupabaseEmailTemplates
// ============================================================================
// Table:   email_templates
// Follows: useSupabase.js conventions (requireSession, formatSupabaseError,
//          normalizeId, camelCase ↔ snake_case mapping)
//
// RLS:
//   SELECT — all authenticated users
//   INSERT/UPDATE/DELETE — admin + admin-pa only
//
// Shape returned by templatesMap:
//   { stage_initiated: { subject, body }, invoice_sent: { subject, body }, ... }
//   Compatible with renderEmailTemplate(eventType, vars, customTemplates)
// ============================================================================

import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../supabaseClient';
import { formatSupabaseError, normalizeId } from './useSupabase';

// ============================================================================
// SESSION GUARD
// ============================================================================
const requireSession = async () => {
  const { data: { session }, error: sessionError } = await supabase.auth.getSession();
  if (sessionError) {
    console.error('[requireSession] Session error:', sessionError);
    throw new Error('Authentication error: ' + sessionError.message);
  }
  if (!session || !session.user) {
    console.error('[requireSession] No active Supabase session');
    throw new Error(
      'Please sign in to perform this action. ' +
      'Note: The "switch user" feature only changes the UI view — ' +
      'it does not change your database permissions. ' +
      'You must sign in with your actual credentials.'
    );
  }
  return session.user.id;
};

// ============================================================================
// DB → APP FIELD MAPPING
// ============================================================================
const mapRow = (r) => ({
  id:           normalizeId(r.id),
  templateName: r.template_name ?? null,
  templateType: r.template_type ?? '',
  stageKey:     r.stage_key     ?? null,
  subject:      r.subject       ?? '',
  body:         r.body          ?? '',
  isDefault:    r.is_default    ?? false,
  version:      r.version       ?? 1,
  isActive:     r.is_active     ?? true,
  createdBy:    r.created_by    ?? null,
  createdAt:    r.created_at    ?? null,
  updatedAt:    r.updated_at    ?? null,
});

// ============================================================================
// HOOK
// ============================================================================
export default function useSupabaseEmailTemplates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ── Fetch all templates ───────────────────────────────────────────────
  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { data, error: fetchErr } = await supabase
      .from('email_templates')
      .select('*')
      .order('template_type', { ascending: true })
      .order('stage_key', { ascending: true, nullsFirst: true });

    if (fetchErr) {
      console.error('[useSupabaseEmailTemplates] fetch error:', fetchErr);
      setError(formatSupabaseError(fetchErr, { action: 'fetch', entity: 'email templates' }));
      setTemplates([]);
    } else {
      setTemplates((data || []).map(mapRow));
    }
    setLoading(false);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // ── Create ────────────────────────────────────────────────────────────
  const create = useCallback(async (templateData) => {
    try {
      const userId = await requireSession();
      const row = {
        template_name: templateData.templateName || null,
        template_type: templateData.templateType,
        stage_key:     templateData.stageKey || null,
        subject:       templateData.subject  || '',
        body:          templateData.body     || '',
        is_default:    templateData.isDefault || false,
        created_by:    userId,
      };

      // If marking as default, unset other defaults for this type first
      if (row.is_default) {
        await supabase
          .from('email_templates')
          .update({ is_default: false })
          .eq('template_type', row.template_type);
      }

      const { data, error: insertErr } = await supabase
        .from('email_templates')
        .insert(row)
        .select()
        .single();

      if (insertErr) {
        const msg = formatSupabaseError(insertErr, { action: 'create', entity: 'email template' });
        return { success: false, error: msg };
      }
      const mapped = mapRow(data);
      if (row.is_default) {
        setTemplates(prev => [
          ...prev.map(t => t.templateType === row.template_type ? { ...t, isDefault: false } : t),
          mapped,
        ]);
      } else {
        setTemplates(prev => [...prev, mapped]);
      }
      return { success: true, data: mapped };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, []);

  // ── Update ────────────────────────────────────────────────────────────
  const update = useCallback(async (id, updates) => {
    try {
      await requireSession();
      const row = {};
      if (updates.templateName !== undefined) row.template_name = updates.templateName;
      if (updates.templateType !== undefined) row.template_type = updates.templateType;
      if (updates.stageKey !== undefined)     row.stage_key     = updates.stageKey || null;
      if (updates.subject !== undefined)      row.subject       = updates.subject;
      if (updates.body !== undefined)         row.body          = updates.body;
      if (updates.isDefault !== undefined)    row.is_default    = updates.isDefault;

      // If marking as default, unset other defaults for this type first
      if (row.is_default && row.template_type) {
        await supabase
          .from('email_templates')
          .update({ is_default: false })
          .eq('template_type', row.template_type)
          .neq('id', id);
      }

      const { data, error: updateErr } = await supabase
        .from('email_templates')
        .update(row)
        .eq('id', id)
        .select()
        .single();

      if (updateErr) {
        const msg = formatSupabaseError(updateErr, { action: 'update', entity: 'email template' });
        return { success: false, error: msg };
      }
      const mapped = mapRow(data);
      if (row.is_default) {
        setTemplates(prev => prev.map(t => {
          if (t.id === id) return mapped;
          if (t.templateType === mapped.templateType) return { ...t, isDefault: false };
          return t;
        }));
      } else {
        setTemplates(prev => prev.map(t => t.id === id ? mapped : t));
      }
      return { success: true, data: mapped };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, []);

  // ── Delete ────────────────────────────────────────────────────────────
  const remove = useCallback(async (id) => {
    try {
      await requireSession();
      const { error: deleteErr } = await supabase
        .from('email_templates')
        .delete()
        .eq('id', id);

      if (deleteErr) {
        const msg = formatSupabaseError(deleteErr, { action: 'delete', entity: 'email template' });
        return { success: false, error: msg };
      }
      setTemplates(prev => prev.filter(t => t.id !== id));
      return { success: true };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, []);

  // ── Upsert (create or update by templateType + stageKey) ──────────────
  const upsert = useCallback(async (templateData) => {
    try {
      const userId = await requireSession();
      const row = {
        template_name: templateData.templateName || null,
        template_type: templateData.templateType,
        stage_key:     templateData.stageKey || null,
        subject:       templateData.subject  || '',
        body:          templateData.body     || '',
        is_default:    templateData.isDefault || false,
        created_by:    userId,
      };
      const { data, error: upsertErr } = await supabase
        .from('email_templates')
        .upsert(row, { onConflict: 'template_type,stage_key' })
        .select()
        .single();

      if (upsertErr) {
        const msg = formatSupabaseError(upsertErr, { action: 'save', entity: 'email template' });
        return { success: false, error: msg };
      }
      const mapped = mapRow(data);
      setTemplates(prev => {
        const idx = prev.findIndex(t => t.templateType === mapped.templateType && t.stageKey === mapped.stageKey);
        if (idx >= 0) {
          const next = [...prev];
          next[idx] = mapped;
          return next;
        }
        return [...prev, mapped];
      });
      return { success: true, data: mapped };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, []);

  // ── Set Default ─────────────────────────────────────────────────────
  const setDefault = useCallback(async (id) => {
    try {
      await requireSession();
      // Find the template to get its type
      const tmpl = templates.find(t => t.id === id);
      if (!tmpl) return { success: false, error: 'Template not found' };

      // Unset other defaults for this type
      await supabase
        .from('email_templates')
        .update({ is_default: false })
        .eq('template_type', tmpl.templateType)
        .neq('id', id);

      // Set this one as default
      const { data, error: updateErr } = await supabase
        .from('email_templates')
        .update({ is_default: true })
        .eq('id', id)
        .select()
        .single();

      if (updateErr) {
        const msg = formatSupabaseError(updateErr, { action: 'set default', entity: 'email template' });
        return { success: false, error: msg };
      }
      const mapped = mapRow(data);
      setTemplates(prev => prev.map(t => {
        if (t.id === id) return mapped;
        if (t.templateType === mapped.templateType) return { ...t, isDefault: false };
        return t;
      }));
      return { success: true, data: mapped };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, [templates]);

  // ── Log template usage ──────────────────────────────────────────────
  const logUsage = useCallback(async ({ templateId, templateType, templateVersion, projectId, invoiceId }) => {
    try {
      const userId = await requireSession();
      await supabase.from('email_template_usage_log').insert({
        template_id:      templateId || null,
        template_type:    templateType,
        template_version: templateVersion || 1,
        project_id:       projectId || null,
        invoice_id:       invoiceId || null,
        created_by:       userId,
      });
    } catch (err) {
      console.error('[useSupabaseEmailTemplates] logUsage error:', err);
    }
  }, []);

  // ── Templates map (for renderEmailTemplate compatibility) ─────────────
  // Shape: { template_type: { subject, body } }
  // Priority: is_default + is_active > first active with content
  const templatesMap = useMemo(() => {
    if (!templates.length) return null;
    const active = templates.filter(t => t.isActive !== false);
    const map = {};
    // First pass: pick defaults
    active.forEach(t => {
      if ((t.subject || t.body) && t.isDefault) {
        map[t.templateType] = { subject: t.subject, body: t.body };
      }
    });
    // Second pass: fill in any type without a default
    active.forEach(t => {
      if ((t.subject || t.body) && !map[t.templateType]) {
        map[t.templateType] = { subject: t.subject, body: t.body };
      }
    });
    return Object.keys(map).length > 0 ? map : null;
  }, [templates]);

  return {
    templates,
    loading,
    error,
    templatesMap,
    refresh,
    create,
    update,
    remove,
    upsert,
    setDefault,
    logUsage,
  };
}
